import React, { FC, useState, useEffect, useCallback } from 'react';
import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import ImageSearchSvg from '../../assets/icons/svg/pageIcons/imageSearch.svg';
import { ViewList as ViewListIcon, GridView as GridViewIcon } from '@mui/icons-material';
import {
    Grid,
    Box,
    FormControl,
    Paper,
    InputBase,
    Button,
    ImageList,
    ImageListItem,
    Checkbox,
    FormControlLabel,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    CircularProgress,
} from '@mui/material';
import ImageSearchV2ServiceQuery from '../../services/query/imageSearchV2Service/imageSearchV2Service.services';
import {
    SCUIImageBatchSearchModalV2,
    SCUIProductListV2,
    SCUIImagePreviewModalV2,
    SCUIImageThumbnailV2,
} from './components';
import { SCUIDownloadImageV2Component } from '../../atomic/atoms/SCUIDownloadImageV2Component/SCUIDownloadImageV2Component';
import { IMGSEARCHV2, imgSearchV2Cls } from './imageSearchV2.classes';
import {
    parseSearchCodesFromText,
    buildImageSearchV2UrlSearch,
    parseImageSearchV2FromUrl,
    parseImageSearchV2ResponseData,
    formatImageSearchV2NotFoundRecords,
    BATCH_SEARCH_LIMIT,
} from './ImageSearchV2.helper';
import { useHistory, useLocation } from 'react-router-dom';

interface DataItem {
    img: string;
    title: string;
}

export enum ImageSearchType {
    Raw = 1,
    Retouched = 2,
}

export const ImageSearchV2: FC<any> = (props: any) => {
    const history = useHistory();
    const location = useLocation();

    const [searchedText, setSearchedText] = useState('');
    const [searchType, setSearchType] = useState<ImageSearchType>(ImageSearchType.Retouched);

    const { t } = useTranslation();
    const [listModalOpen, setListModalOpen] = useState(false);
    const [datas, setDatas] = React.useState<any>();
    const [notFoundRecords, setNotFoundRecords] = React.useState<any>();
    const [loading, setLoading] = React.useState(false);
    const [downloadLoading, setDownloadLoading] = React.useState(false);
    const [checkboxStatus, setCheckboxStatus] = React.useState(false);
    const [checkBoxTitle, setCheckboxTitle] = React.useState('Tümünü Seç');
    const [selectedItems, setSelectedItems] = useState<DataItem[]>([]);
    const [view, setView] = useState('list');
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const isBusy = loading || downloadLoading;

    const handleCloseModal = () => {
        setListModalOpen(false);
    };

    const handleImageClick = (item: any) => {
        if (isBusy) {
            return;
        }
        if (selectedItems.includes(item)) {
            setSelectedItems(selectedItems.filter((i) => i !== item));
        } else {
            setSelectedItems([...selectedItems, item]);
        }
    };

    const handleSelectClick = (item: boolean) => {
        if (isBusy) {
            return;
        }
        setCheckboxStatus(item);
        setCheckboxTitle(item ? 'Tümünü Kaldır' : 'Tümünü Seç');
        if (item) {
            setSelectedItems([...selectedItems, ...datas.map((item: any) => item)]);
        } else {
            setSelectedItems([]);
        }
    };

    const handleListViewClick = () => {
        if (isBusy) {
            return;
        }
        setView('list');
    };
    const handleGridViewClick = () => {
        if (isBusy) {
            return;
        }
        setView('grid');
    };

    const parseResponse = (res: any) => {
        if (!res) {
            Swal.fire(t('general.noDataFound'), ' ', 'error');
            return;
        }

        const parseData = parseImageSearchV2ResponseData(res.data || []);

        if (parseData.length === 0) {
            Swal.fire(t('general.noDataFound'), t('imageSearchV2.noPhotosFound'), 'error');
            return;
        }

        setDatas(parseData);

        if (res.notFoundRecords && res.notFoundRecords.length > 0) {
            setNotFoundRecords(formatImageSearchV2NotFoundRecords(res.notFoundRecords));
        }
    };

    const searchImages = useCallback(
        async (codes: string[], type: ImageSearchType = searchType, syncUrl = true) => {
            if (codes.length === 0) {
                return;
            }

            if (codes.length > BATCH_SEARCH_LIMIT) {
                Swal.fire(
                    t('general.warning'),
                    t('imageSearchV2.batchSearchLimitWarning', { max: BATCH_SEARCH_LIMIT, count: codes.length }),
                    'warning'
                );
                return;
            }

            if (syncUrl) {
                history.replace({
                    pathname: location.pathname,
                    search: buildImageSearchV2UrlSearch(type, codes),
                });
            }

            setLoading(true);
            setDatas('');
            setNotFoundRecords('');
            setSelectedItems([]);

            const obj = {
                specialCode: codes,
                searchType: type,
            };

            try {
                const res = await ImageSearchV2ServiceQuery.GetImageData(obj);
                parseResponse(res);
            } finally {
                setLoading(false);
            }
        },
        [searchType, history, location.pathname, t]
    );

    useEffect(() => {
        const urlState = parseImageSearchV2FromUrl(location.search);
        setSearchType(urlState.searchType);
        setSearchedText(urlState.searchedText);

        if (urlState.codes.length > 0) {
            searchImages(urlState.codes, urlState.searchType, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const listele = async () => {
        const codes = parseSearchCodesFromText(searchedText);
        await searchImages(codes, searchType);
    };

    const GetImages = async () => {
        const codes = parseSearchCodesFromText(searchedText);
        await searchImages(codes, searchType);
    };

    const handleClearSearch = () => {
        setDatas('');
        setSearchedText('');
        setNotFoundRecords('');
        history.replace({ pathname: location.pathname, search: '' });
    };

    return (
        <SCUIContent>
            <Box className={IMGSEARCHV2.page}>
                <Grid size={{ sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Grid
                        className={IMGSEARCHV2.breadcrumbRow}
                        container
                        direction="row"
                        spacing={2}
                        sx={{ marginLeft: '0.5rem' }}
                    >
                        <div className={IMGSEARCHV2.breadcrumbSection}>
                            <SCUIBreadCrumb
                                title={t('imageSearchV2.pageTitle')}
                                componentLink={'/ImageSearchV2'}
                                style={{ cursor: 'pointer' }}
                                onClick={handleClearSearch}
                            />
                        </div>
                        <div className={IMGSEARCHV2.loadingSection}>
                            <SCUILoadingLinear display={loading} />
                        </div>
                    </Grid>
                    <Grid size={{ sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Grid sx={{ marginTop: '20px' }} size={{ sm: 12, md: 12, lg: 12, xl: 12 }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                                    <CircularProgress size={36} />
                                </Box>
                            ) : datas && datas.length > 0 ? (
                                <SCUIDownloadImageV2Component
                                    selectedItems={selectedItems}
                                    downloadLoading={downloadLoading}
                                    setDownloadLoading={setDownloadLoading}
                                    disabled={isBusy}
                                >
                                    {({ statusPanel, button }) => (
                                <Grid container>
                                    {statusPanel && (
                                        <Grid size={{ sm: 12, md: 12, lg: 12, xl: 12 }}>
                                            {statusPanel}
                                        </Grid>
                                    )}
                                    <Grid
                                        className={IMGSEARCHV2.resultsToolbar}
                                        size={{ sm: 12, md: 12, lg: 12, xl: 12 }}
                                        sx={{
                                            mb: 2,
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 1.5,
                                        }}
                                    >
                                        <FormControlLabel
                                            className={IMGSEARCHV2.labelSelectAllWrap}
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '1.3rem',
                                                    fontWeight: 500,
                                                    color: '#1f2937',
                                                },
                                            }}
                                            label={checkBoxTitle}
                                            control={
                                                <Checkbox
                                                    className={IMGSEARCHV2.inputCheckboxSelectAll}
                                                    checked={checkboxStatus}
                                                    onChange={() => handleSelectClick(!checkboxStatus)}
                                                    title={checkBoxTitle}
                                                    disabled={isBusy}
                                                />
                                            }
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Box
                                                className={IMGSEARCHV2.inputIconListView}
                                                onClick={handleListViewClick}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '10px',
                                                    bgcolor: view === 'list' ? '#e0f2fe' : '#ffffff',
                                                    border: `1px solid ${view === 'list' ? '#38bdf8' : '#dbeafe'}`,
                                                    cursor: 'pointer',
                                                    opacity: isBusy ? 0.5 : 1,
                                                    pointerEvents: isBusy ? 'none' : 'auto',
                                                }}
                                            >
                                                <ViewListIcon
                                                    sx={{
                                                        fontSize: '2.1rem',
                                                        color: view === 'list' ? '#0284c7' : '#64748b',
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                className={IMGSEARCHV2.inputIconGridView}
                                                onClick={handleGridViewClick}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '10px',
                                                    bgcolor: view === 'grid' ? '#e0f2fe' : '#ffffff',
                                                    border: `1px solid ${view === 'grid' ? '#38bdf8' : '#dbeafe'}`,
                                                    cursor: 'pointer',
                                                    opacity: isBusy ? 0.5 : 1,
                                                    pointerEvents: isBusy ? 'none' : 'auto',
                                                }}
                                            >
                                                <GridViewIcon
                                                    sx={{
                                                        fontSize: '2.1rem',
                                                        color: view === 'grid' ? '#0284c7' : '#64748b',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box
                                            className={IMGSEARCHV2.downloadWrap}
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                        >
                                            {button}
                                        </Box>
                                    </Grid>
                                    <Grid
                                        className={IMGSEARCHV2.resultsSummary}
                                        size={{ sm: 12, md: 12, lg: 12, xl: 12 }}
                                    >
                                        <Box sx={{ mb: 2 }}>
                                            <Typography sx={{ fontSize: '1.3rem', color: '#334155' }}>
                                                <strong>{t('imageSearchV2.searchResults')}: </strong>
                                                {t('imageSearchV2.totalResults', { count: datas.length })}
                                            </Typography>
                                            <Typography
                                                className={IMGSEARCHV2.notFoundHint}
                                                sx={{
                                                    color: '#64748b',
                                                    fontStyle: 'italic',
                                                    fontSize: '1.2rem',
                                                    mt: 0.5,
                                                }}
                                            >
                                                {notFoundRecords &&
                                                    t('imageSearchV2.notFoundRecords', {
                                                        records: notFoundRecords.join(', '),
                                                    })}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    {view === 'grid' ? (
                                        <Grid size={{ sm: 12, md: 12, lg: 12, xl: 12 }}>
                                            <ImageList
                                                className={IMGSEARCHV2.gridImageList}
                                                sx={{ height: 500, marginBottom: '20px', overflow: 'visible' }}
                                                cols={5}
                                                rowHeight={400}
                                            >
                                                {datas.map((item: any) => {
                                                    const imageSrc =
                                                        item.imagePathIndexMetas.imageSrc ||
                                                        item.imagePathIndexMetas.uri ||
                                                        '';
                                                    const gridSrc = item.imagePathIndexMetas.uri
                                                        ? `${imageSrc}?w=750&h=800&fit=crop&auto=format`
                                                        : imageSrc;
                                                    return (
                                                        <ImageListItem
                                                            className={IMGSEARCHV2.gridImageListItem}
                                                            key={imageSrc || item.id}
                                                            sx={{
                                                                position: 'relative',
                                                                borderRadius: '10px',
                                                                zIndex: 2,
                                                            }}
                                                        >
                                                            <SCUIImageThumbnailV2
                                                                src={gridSrc}
                                                                previewSrc={imageSrc}
                                                                onPreview={setPreviewImage}
                                                                onSelect={() => handleImageClick(item)}
                                                                disabled={isBusy}
                                                                className={IMGSEARCHV2.gridImageListItem}
                                                                containerStyle={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '10px',
                                                                }}
                                                                imageStyle={{ borderRadius: '10px' }}
                                                            >
                                                                <Checkbox
                                                                    className={IMGSEARCHV2.inputCheckboxGridItem}
                                                                    checked={selectedItems.includes(item)}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleImageClick(item);
                                                                    }}
                                                                    disabled={isBusy}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 10,
                                                                        left: 10,
                                                                        zIndex: 2,
                                                                        paddingTop: 6,
                                                                        transform: 'scale(2)',
                                                                    }}
                                                                />
                                                            </SCUIImageThumbnailV2>
                                                        </ImageListItem>
                                                    );
                                                })}
                                            </ImageList>
                                        </Grid>
                                    ) : (
                                        <SCUIProductListV2
                                            products={datas}
                                            selectedItems={selectedItems}
                                            setSelectedItems={setSelectedItems}
                                            onPreviewImage={setPreviewImage}
                                            disabled={isBusy}
                                        />
                                    )}
                                </Grid>
                                    )}
                                </SCUIDownloadImageV2Component>
                            ) : (
                                <div
                                    className={IMGSEARCHV2.emptyStateWrap}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <SCUINoRecordComponent
                                        img={ImageSearchSvg}
                                        text={t('imageSearchV2.noRecordText')}
                                    />
                                    <Box className={IMGSEARCHV2.searchTypeToggle} sx={{ marginTop: '2rem' }}>
                                        <ToggleButtonGroup
                                            exclusive
                                            value={searchType}
                                            onChange={(_, value) => {
                                                if (value !== null) {
                                                    setSearchType(value);
                                                }
                                            }}
                                            color="primary"
                                            size="small"
                                            sx={{
                                                '& .MuiToggleButton-root': {
                                                    fontSize: '1.2rem',
                                                    padding: '6px 16px',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    height: '36px',
                                                },
                                            }}
                                        >
                                            <ToggleButton value={ImageSearchType.Raw} disabled={isBusy}>
                                                {t('imageSearchV2.rawImage')}
                                            </ToggleButton>
                                            <ToggleButton value={ImageSearchType.Retouched} disabled={isBusy}>
                                                {t('imageSearchV2.retouchedImage')}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <div className={IMGSEARCHV2.searchPaperWrap} style={{ marginTop: '2rem' }}>
                                        <Paper
                                            className={imgSearchV2Cls(
                                                IMGSEARCHV2.searchPaper,
                                                'textFieldWithButtonPaper textButtonSelect'
                                            )}
                                        >
                                            <InputBase
                                                className={IMGSEARCHV2.inputSearchCodes}
                                                sx={{ ml: 1, flex: 1, fontSize: '1.6rem' }}
                                                fullWidth
                                                value={searchedText}
                                                disabled={isBusy}
                                                onChange={(e) => setSearchedText(e.target.value)}
                                                onKeyDown={props.onKeyPressTextField}
                                            />
                                            <Button
                                                className={imgSearchV2Cls(
                                                    IMGSEARCHV2.inputButtonSearch,
                                                    'textFieldWithButton'
                                                )}
                                                color="primary"
                                                type="button"
                                                onClick={GetImages}
                                                disabled={isBusy}
                                            >
                                                {loading ? (
                                                    <CircularProgress size={18} color="inherit" />
                                                ) : (
                                                    t('general.search')
                                                )}
                                            </Button>
                                        </Paper>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: 'block',
                                                mt: 0.75,
                                                fontSize: '1.1rem',
                                                textAlign: 'left',
                                            }}
                                        >
                                            {t('imageSearchV2.optionHint')}
                                        </Typography>
                                    </div>
                                    <FormControl
                                        className={IMGSEARCHV2.batchSearchFormWrap}
                                        style={{ width: 320, marginRight: '10px', marginTop: '1.5rem' }}
                                    >
                                        <SCUIButton
                                            className={IMGSEARCHV2.inputButtonBatchSearch}
                                            text={t('general.batchSearch')}
                                            type="secondary"
                                            sizeType="fixed"
                                            size={'m'}
                                            disabled={isBusy}
                                            onClick={() => setListModalOpen(true)}
                                        />
                                        <SCUIImageBatchSearchModalV2
                                            open={listModalOpen}
                                            onClose={handleCloseModal}
                                            searchedText={searchedText}
                                            setSearchedText={setSearchedText}
                                            listele={listele}
                                            text={t('imageSearchV2.optionHint')}
                                        />
                                    </FormControl>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <SCUIImagePreviewModalV2 imageUrl={previewImage} onClose={() => setPreviewImage(null)} />
        </SCUIContent>
    );
};
